import React, { Component } from 'react';
import { connect } from 'react-redux';
import Navigation from './Navigation';
import Logo from '../Elements/SVG/SVG';
import { translate } from '../../utils/locale';
import { addEvent, removeEvent, hasClass, addClass, removeClass, offset, scrollTo, width, height } from '../../utils/dom';
import { removeNotification, changeLanguage, openCart, openMenu, goToPage, clientInit } from '../../actions/environment';
import s from './Header.scss';

class Header extends Component {
	constructor(props) {
		super(props);

		this.sections = [];
		this.pinned = false;
		this.headerElement = React.createRef();
		this.canvasElement = React.createRef();

		this.state = {
			grid: false,
			currentSection: null,
			pinNavigation: 0,
		};
	}

	componentDidMount() {
		if (typeof window !== 'undefined') {
			const $html = document.querySelector('html');

			this.props.clientInit();

			// Wait for page to be rendered with all relevant elements.
			const initiateSiteListeners = () => {
				if (!this.headerElement.current || !this.canvasElement.current) {
					setTimeout(initiateSiteListeners, 200);
				}

				this.initiateCanvas();
				this.scrollToHash();
				this.mapSections();

				this.setState({ currentSection: 0 });
			};

			initiateSiteListeners();

			if ('ontouchstart' in document.documentElement) {
				removeClass($html, 'no-touchevents');
				addClass($html, 'touchevents');
			}

			if (hasClass($html, 'no-touchevents')) {
				addEvent(window, 'touchstart.touchTest', () => {
					removeClass($html, 'no-touchevents');
					addClass($html, 'touchevents');
					removeEvent(window, 'touchstart.touchTest');
				});
			}

			addEvent(document.querySelector('body'), 'click.linkCapture', (e) => {
				const $link = e.target.closest('a');

				if ($link && hasClass($link, 'linkCapture') && !/(http|mailto|tel)/.test($link.getAttribute('href'))) {
					e.preventDefault();
					this.props.goToPage(e.target.getAttribute('href'));
				}
			});

			addEvent(window, 'keydown.grid', (e) => {
				if (e.shiftKey && e.ctrlKey && e.key === 'A') {
					// ctrl + shift + a
					this.setState((prevState) => ({ grid: !prevState.grid }));
				} else if (this.state.grid && e.key === 'Escape') {
					this.setState({ grid: false });
				}
			});

			addEvent(window, 'resize.updateSections', () => {
				window.requestAnimationFrame(() => {
					this.mapSections();
				});
			});

			addEvent(window, 'scroll.navigationSections', () => {
				window.requestAnimationFrame(() => {
					this.checkPosition();
				});
			});
		}
	}

	componentWillUnmount() {
		removeEvent(window, 'keydown.grid');
		removeEvent(window, 'scroll.navigationSections');
		removeEvent(window, 'resize.canvas');
		removeEvent(document, 'mouseout.trackMouseLeft');
		removeEvent(document, 'mousemove.trackMouse');
	}

	notificationRemovedHandler = () => {}

	initiateCanvas = () => {
		const distance = (x, y) => Math.sqrt((x * x) + (y * y));
		const fills = ['rgb(0, 0, 4)', 'rgb(2, 2, 11)', 'rgb(5, 4, 22)', 'rgb(9, 7, 32)', 'rgb(14, 11, 43)', 'rgb(20, 14, 54)', 'rgb(26, 16, 66)', 'rgb(33, 17, 78)', 'rgb(41, 17, 90)', 'rgb(51, 16, 103)', 'rgb(59, 15, 112)', 'rgb(68, 15, 118)', 'rgb(76, 17, 122)', 'rgb(84, 19, 125)', 'rgb(92, 22, 127)', 'rgb(100, 26, 128)', 'rgb(107, 29, 129)', 'rgb(117, 33, 129)', 'rgb(124, 35, 130)', 'rgb(132, 38, 129)', 'rgb(140, 41, 129)', 'rgb(148, 44, 128)', 'rgb(156, 46, 127)', 'rgb(165, 49, 126)', 'rgb(173, 52, 124)', 'rgb(183, 55, 121)', 'rgb(191, 58, 119)', 'rgb(199, 61, 115)', 'rgb(207, 64, 112)', 'rgb(214, 69, 108)', 'rgb(222, 73, 104)', 'rgb(228, 79, 100)', 'rgb(234, 86, 97)', 'rgb(239, 93, 94)', 'rgb(244, 103, 92)', 'rgb(247, 112, 92)', 'rgb(249, 121, 93)', 'rgb(251, 131, 95)', 'rgb(252, 140, 99)', 'rgb(253, 150, 104)', 'rgb(254, 159, 109)', 'rgb(254, 169, 115)', 'rgb(254, 180, 123)', 'rgb(254, 189, 130)', 'rgb(254, 198, 138)', 'rgb(254, 207, 146)', 'rgb(254, 216, 154)', 'rgb(253, 226, 163)', 'rgb(253, 235, 172)', 'rgb(252, 244, 182)'];

		const canvasContainer = this.canvasElement.current;
		const canvas = this.canvasElement.current.getElementsByTagName('canvas')[0];
		const ctx = canvas.getContext('2d');
		const startAngle = 0;
		const endAngle = Math.PI * 2;
		const pointerDetails = {
			animating: false,
			active: false,
			x: [],
			y: [],
			time: [],
			direction: [],
			directionDiff: [],
			velocity: [],
		};

		const fpsInterval = 1000 / 60;
		const startTime = window.performance.now();

		const vh = 0.6; // viewport height for canvas before random dots.
		const grid = 60;
		const pull = 2;
		const fadeLength = 15;
		const pixelRadius = 2;
		const transitionLength = 400; // ~0.4s from start / end
		const cursorAttraction = 240;
		const samples = 24;
		const randomDotMatrix = [];

		// let frameCount = 0;
		let lastFrame = startTime;
		let currentCursorAttraction = 0;
		let farthestPointOfAttraction = 0;
		let farthestPointOfAttractionPull = 0;
		let windowWidth = (document.body.clientWidth * 2) + (cursorAttraction * 4);
		let windowHeight = (window.innerHeight * vh * 2) + (cursorAttraction * 2) + (grid * (fadeLength * 2));
		let stopEndTransition = null;
		let stopStartTransition = null;
		let startTransition = null;
		let endTransition = null;

		/*
			Animate back to positions when cursor leaves page.
		*/

		const createRandomDotMatrix = () => {
			for (let i = 1; i < (fadeLength + 1); i += 1) {
				if (!randomDotMatrix[i]) randomDotMatrix[i] = [];
				for (let j = 1; j < Math.ceil(windowWidth / grid); j += 1) {
					if ((i % 2 === 0 && j % 2 === 0) || (i % 2 === 1 && j % 2 === 1)) continue;
					if (randomDotMatrix[i][j] === 0 || randomDotMatrix[i][j] === 1) continue;
					randomDotMatrix[i][j] = Math.random() * (fadeLength + 1) > i ? 1 : 0;
				}
			}
		};

		const render = () => {
			const now = window.performance.now();
			const pX = pointerDetails.x[0];
			const pY = pointerDetails.y[0];
			let fadeCounter = 0;

			// TODO: Should be set up to carry on from where it was.
			if (endTransition && now - endTransition < transitionLength) {
				currentCursorAttraction = cursorAttraction * (1 - ((now - endTransition) / transitionLength));
			} else if (startTransition && now - startTransition < transitionLength) {
				currentCursorAttraction = cursorAttraction * ((now - startTransition) / transitionLength);
			}

			farthestPointOfAttraction = Math.round(distance(currentCursorAttraction, currentCursorAttraction));
			farthestPointOfAttractionPull = farthestPointOfAttraction ** pull;

			ctx.clearRect(0, 0, windowWidth, windowHeight);
			for (let i = 1; i < Math.ceil(windowHeight / grid); i += 1) {
				const lastRows = i >= (((Math.ceil(windowHeight / grid) % 2) + Math.ceil(windowHeight / grid)) - fadeLength);
				if (lastRows) fadeCounter += 1;

				for (let j = 1; j < Math.ceil(windowWidth / grid); j += 1) {
					if ((i % 2 === 0 && j % 2 === 0) || (i % 2 === 1 && j % 2 === 1)) continue;
					if (lastRows && randomDotMatrix[fadeCounter] && randomDotMatrix[fadeCounter][j] === 0) continue;

					let x = j * grid;
					let y = i * grid;
					let fillStyle = fills[0];

					if (pointerDetails.animating) {
						const distanceFromCursor = distance(y - pY, x - pX);
						if (farthestPointOfAttraction - Math.abs(distanceFromCursor) > 0) {
							// (pull * (Math.abs(distanceFromCursor) / farthestPointOfAttraction)).toFixed(2)
							const attractionStrengthPercentage = 1 - ((Math.abs(distanceFromCursor) ** pull) / farthestPointOfAttractionPull);
							fillStyle = fills[Math.floor((attractionStrengthPercentage / 2) * 100)];

							const xDistanceToMovePointBy = (x - pX) * attractionStrengthPercentage;
							const yDistanceToMovePointBy = (y - pY) * attractionStrengthPercentage;

							x -= xDistanceToMovePointBy;
							y -= yDistanceToMovePointBy;
						}
					}

					ctx.fillStyle = fillStyle;
					ctx.beginPath();
					ctx.arc(x, y, pixelRadius, startAngle, endAngle);
					ctx.fill();
				}
			}

			if (pointerDetails.active) {
				ctx.fillStyle = 'rgb(0, 0, 0)';
				ctx.beginPath();
				ctx.fillRect(pX, pY - 10, 1, 21);
				ctx.fillRect(pX - 10, pY, 21, 1);
				ctx.fill();

				/*
				const direction = (pointerDetails.direction.reduce((acc, d) => acc + d, 0) / pointerDetails.direction.length) - 10;
				const directionDiff = pointerDetails.directionDiff.reduce((acc, dD) => acc + dD, 0) / pointerDetails.directionDiff.length;
				const velocity = pointerDetails.velocity.reduce((acc, v) => acc + v, 0) / pointerDetails.velocity.length;

				// predicted mouse pointer.
				for (let i = 0; i < 5; i++) {
					const fX = pX - (Math.sin(direction + (directionDiff * i)) * (velocity * i) * 50);
					const fY = pY - (Math.cos(direction + (directionDiff * i)) * (velocity * i) * 50);
					ctx.strokeStyle = fills[fills.length - (Math.round(fills.length / 5) * i)];
					ctx.beginPath();
					ctx.arc(fX, fY, 4, startAngle, endAngle);
					ctx.stroke();
				}
				*/
			}
		};

		const resetInputs = () => {
			pointerDetails.animating = false;
			pointerDetails.active = false;
			pointerDetails.x = [];
			pointerDetails.y = [];
			pointerDetails.time = [];
			pointerDetails.direction = [];
			pointerDetails.directionDiff = [];
			pointerDetails.velocity = [];
		};

		const checkInput = (inputX, inputY) => {
			// Save position, direction and velocity of mouse for animating dots after input finished.
			pointerDetails.x = [((inputX * 2) + (cursorAttraction * 2)), ...pointerDetails.x].slice(0, samples);
			pointerDetails.y = [((inputY * 2) + (cursorAttraction * 2)), ...pointerDetails.y].slice(0, samples);
			pointerDetails.time = [window.performance.now(), ...pointerDetails.time].slice(0, samples);

			if (pointerDetails.x.length > 2) {
				const x = pointerDetails.x[0] - pointerDetails.x[1];
				const y = pointerDetails.y[0] - pointerDetails.y[1];
				const theta = Math.atan2(-x, -y);
				// Velocity measured in ms per pixel.
				pointerDetails.velocity = [(distance(x, y) / (pointerDetails.time[0] - pointerDetails.time[1])), ...pointerDetails.velocity].slice(0, samples);
				pointerDetails.direction = [theta + 10, ...pointerDetails.direction].slice(0, samples);
				if (pointerDetails.direction.length > 2) pointerDetails.directionDiff = [(pointerDetails.direction[0] - pointerDetails.direction[1]), ...pointerDetails.directionDiff].slice(0, samples);
			}
		};

		const transitionOut = () => {
			// Update pointerDetails on last velocity.
			const direction = (pointerDetails.direction.reduce((acc, d) => acc + d, 0) / pointerDetails.direction.length) - 10;
			const directionDiff = pointerDetails.directionDiff.reduce((acc, dD) => acc + dD, 0) / pointerDetails.directionDiff.length;
			const velocity = pointerDetails.velocity.reduce((acc, v) => acc + v, 0) / pointerDetails.velocity.length;

			pointerDetails.x = [(pointerDetails.x[0] - (Math.sin(direction + directionDiff) * velocity * 5)), ...pointerDetails.x];
			pointerDetails.y = [(pointerDetails.y[0] - (Math.cos(direction + directionDiff) * velocity * 5)), ...pointerDetails.y];
		};

		const animate = () => {
			if (!pointerDetails.animating) return;

			window.requestAnimationFrame(animate);

			const currentFrame = window.performance.now();
			const elapsed = currentFrame - lastFrame;

			// if enough time has elapsed, draw the next frame
			if (elapsed > fpsInterval) {
				// Get ready for next frame by setting lastFrame = currentFrame, but...
				// Also, adjust for fpsInterval possibly not being a multiple of 16.67
				lastFrame = currentFrame - (elapsed % fpsInterval);

				if (endTransition && currentFrame - endTransition < transitionLength) transitionOut();

				render();

				// TESTING...Report #seconds since start and achieved fps.
				// const sinceStart = now - startTime;
				// const currentFps = Math.round(1000 / (sinceStart / ++frameCount) * 100) / 100;
				// $results.text(`Elapsed time=${Math.round(sinceStart / 1000 * 100) / 100} secs @ ${currentFps} fps.`);
			}
		};

		/*
			SETUP
		*/

		canvas.width = windowWidth;
		canvas.height = windowHeight;
		canvas.style.width = `calc(100% + ${cursorAttraction * 2}px)`;
		canvas.style.height = `calc(${vh * 100}vh + ${cursorAttraction + (grid * fadeLength)}px)`;
		canvas.style.left = `${(cursorAttraction * -1)}px`;
		canvas.style.top = `${(cursorAttraction * -1)}px`;

		canvasContainer.style.height = `calc(${vh * 100}vh + ${grid * fadeLength}px)`;

		ctx.fillStyle = 'rgb(0, 0, 0)';
		ctx.lineWidth = 2;

		/*
			INSTANTIATION
		*/

		createRandomDotMatrix();
		render();

		/*
			EVENTS
		*/
		addEvent(window, 'resize.canvas', () => {
			window.requestAnimationFrame(() => {
				windowWidth = (document.body.clientWidth * 2) + (cursorAttraction * 4);
				windowHeight = (window.innerHeight * vh * 2) + (cursorAttraction * 2) + (grid * (fadeLength * 2));

				canvas.width = windowWidth;
				canvas.height = windowHeight;

				createRandomDotMatrix();
				render();
			});
		});

		addEvent(document, 'mouseout.trackMouseLeft', (e) => {
			// wind down the dots, return back to original position using cursor attraction + velocity, direction
			if (e.target.nodeName !== 'CANVAS') return;

			pointerDetails.active = false;
			endTransition = window.performance.now();

			stopEndTransition = setTimeout(() => {
				stopEndTransition = null;
				pointerDetails.animating = false;
				endTransition = null;
				resetInputs();
			}, transitionLength);

			if (stopStartTransition) {
				clearTimeout(stopStartTransition);
				stopStartTransition = null;
				startTransition = null;
			}
		});

		addEvent(document, 'mousemove.trackMouse', (e) => {
			window.requestAnimationFrame(() => {
				// Debounce out. Seems like rAF is running one more frame even after mouse left causing a race condition.
				if (endTransition && window.performance.now() - endTransition < 100) return;
				checkInput(e.pageX, e.pageY);

				if (stopEndTransition) {
					clearTimeout(stopEndTransition);
					stopEndTransition = null;
					resetInputs();
					endTransition = null;
					checkInput(e.pageX, e.pageY);
				}

				if (!pointerDetails.active) {
					pointerDetails.active = true;
					pointerDetails.animating = true;

					// Transition in.
					startTransition = window.performance.now();
					stopStartTransition = setTimeout(() => {
						stopStartTransition = null;
						startTransition = null;
					}, transitionLength);

					animate();
				}
			});
		});
	}

	scrollNavigation = (e) => {
		e.preventDefault();
		this.mapSections(); // Ensure sections are updated before scrolling.
		const link = e.currentTarget.getAttribute('href');
		const position = link === '/' ? [{ offset: -1 }] : this.sections.filter((section) => link.replace(/#/, '') === section.id);
		if (position.length > 0) scrollTo(500, position[0].offset + 1);
	}

	checkPosition = () => {
		const documentHeight = height(document);
		const top = window.pageYOffset || document.documentElement.scrollTop;
		const middle = top + (window.innerHeight / 2);
		const bottom = top + window.innerHeight;
		const pinNavigationPoint = documentHeight - (width(this.headerElement.current.getElementsByTagName('nav')[0]) + height(document.getElementsByTagName('footer')[0]) + 58);

		this.mapSections();

		if (this.sections.length > 0) {
			if (bottom > documentHeight - 20) {
				const finalSection = this.sections[this.sections.length - 1];
				this.setState({ currentSection: finalSection.id });
				if (typeof window !== 'undefined' && finalSection.id) window.history.replaceState({}, '', `#${finalSection.id}`);
			} else {
				this.sections.forEach((section, i) => {
					if (middle > section.offset && middle <= this.sections?.[i + 1]?.offset && this.state.currentSection !== section.id) {
						this.setState({ currentSection: section.id });
						if (typeof window !== 'undefined' && section.id) window.history.replaceState({}, '', `#${section.id}`);
						else window.history.replaceState({}, '', '/');
					}
				});
			}
		}

		if (!this.pinned && top > pinNavigationPoint) {
			this.setState({ pinNavigation: pinNavigationPoint });
			this.pinned = true;
		} else if (this.pinned && top < pinNavigationPoint) {
			this.setState({ pinNavigation: 0 });
			this.pinned = false;
		}
	}

	mapSections = (language) => {
		const navigationLinks = this.props?.navigation?.header;

		if (navigationLinks) {
			this.sections[0] = { id: 'coalesce-create', offset: 0 };
			navigationLinks.forEach((link, i) => {
				const id = translate(this.props.setup, link, 'uri').replace('#', '');
				if (language) this.sections[i + 1].id = id;
				else this.sections[i + 1] = { id, offset: offset(document.getElementById(id)).top - (window.innerHeight / 5) };
			});
		}
	}

	scrollToHash = () => {
		let imagesLoaded = null;
		const hashLocation = this.props.hashLocation || window.location.hash;

		const allLoaded = () => {
			const imgs = document.body.querySelectorAll('img');
			imagesLoaded = [...imgs].length;

			[...imgs].forEach((img) => {
				if (img.complete) imagesLoaded -= 1;
			});

			if (this.pageElement !== undefined && imagesLoaded === 0) {
				if (hashLocation) {
					const position = this.sections.filter((section) => hashLocation.replace('#', '') === section.id);
					if (position.length > 0) scrollTo(500, position[0].offset);
				}

				this.mapSections();
			} else {
				setTimeout(allLoaded, 50);
			}
		};

		allLoaded();
	}

	changeLanguage = (e) => {
		const changeTo = e.currentTarget.dataset.language;
		this.props.changeLanguage(changeTo);
		this.mapSections(changeTo);
		this.checkPosition();
	}

	render() {
		const { properties, navigation, setup, pageType, location } = this.props;
		const { grid, currentSection, pinNavigation } = this.state;
		if (properties.siteInitiated === false || properties.siteClosed === true) return null;
		const startPage = location.pathname.split('/').filter((uri) => !!uri).length === 1;

		return (
			<>
				<header id="coalesce-create" className={s.header} ref={this.headerElement}>
					<div className={s.header_fixedContainer}>
						<div className={s.header_links}>
							{
								startPage && (
									<Navigation navigation={navigation} location={location} pageType={pageType} setup={setup} scrollNavigation={this.scrollNavigation} changeLanguage={this.changeLanguage} currentSection={currentSection} pinNavigation={pinNavigation} />
								)
							}
						</div>
					</div>
				</header>
				<div className={s.logo}>
					<Logo />
				</div>
				<div className={s.canvas} ref={this.canvasElement}>
					<div className={s.canvas_inner}>
						<canvas className={s.canvas_inner_background} />
					</div>
				</div>
				{ grid && <div className={s.grid}><div /></div> }
			</>
		);
	}
}

const mapState = ({ environment, location }) => {
	const { properties, navigation, setup, pageType } = environment;
	return { properties, navigation, setup, pageType, location };
};

export default connect(mapState, { removeNotification, changeLanguage, openCart, openMenu, goToPage, clientInit })(Header);
